import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import VideoSection from './components/VideoSection';
import ChannelsSection from './components/ChannelsSection';
import MetaPartnerSection from './components/MetaPartnerSection';
import DemoSection from './components/DemoSection';
import PlansSection from './components/PlansSection';
import ClientsSection from './components/ClientsSection';
import Footer from './components/Footer';
import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import AboutUs from './components/AboutUs';
import CareersSection from './components/CareersSection';
import DocumentationPage from './components/DocumentationPage';
import DocumentDetailPage from './components/DocumentDetailPage';
import ChangelogPage from './components/ChangelogPage';
import ThankYouPage from './components/ThankYouPage';
import CustomDevelopmentPage from './components/CustomDevelopmentPage';
import BlogPage from './components/BlogPage';
import BlogPostPage from './components/BlogPostPage';
import SEO from './components/SEO';
import ChatWidget from './components/ChatWidget';
// Importar páginas dedicadas
import PlanesPage from './pages/PlanesPage';
import CanalesPage from './pages/CanalesPage';
import IntegracionesPage from './pages/IntegracionesPage';
import HerramientasPage from './pages/HerramientasPage';
import PreguntasFrecuentesPage from './pages/PreguntasFrecuentesPage';
import DemoPage from './pages/DemoPage';
import PlanRunamaticPage from './pages/PlanRunamaticPage';
import CampanasPublicitariasPage from './pages/CampanasPublicitariasPage';
import PresupuestadorPage from './pages/PresupuestadorPage';
import PartnersPage from './pages/PartnersPage';
import DevLandingPage from './pages/DevLandingPage';
import './styles/App.css';

// Scrolls to the top of the page on every route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
};

// Home Page Component with Hash Navigation
const HomePageContent = () => {
  const seoData = {
    title: null,
    description: 'Runamatic automatiza la atención al cliente en WhatsApp, Instagram, Facebook, TikTok y más canales con inteligencia artificial. CRM omnicanal, chatbots IA y coexistencia WhatsApp Business para empresas latinoamericanas. Meta Business Partner oficial.',
    keywords: 'automatización atención al cliente, chatbot WhatsApp Business, automatizar WhatsApp empresa, CRM omnicanal, chatbot IA para empresas, automatizar Instagram mensajes, automatizar Facebook, coexistencia WhatsApp, Meta Business Partner, automatización ventas WhatsApp, chatbot inteligencia artificial, plataforma omnicanal Latinoamérica',
    canonical: '/',
    ogTitle: 'Runamatic - Automatización Inteligente de Atención al Cliente',
    ogDescription: 'Automatiza WhatsApp, Instagram, Facebook y más con IA. CRM omnicanal, chatbots inteligentes y coexistencia WhatsApp Business. Meta Business Partner para empresas en Latinoamérica.',
    ogImage: 'https://runamatic.io/og-image.png',
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Runamatic",
        "url": "https://runamatic.io",
        "logo": {
          "@type": "ImageObject",
          "url": "https://runamatic.io/LogoNegro.png",
          "width": 200,
          "height": 60
        },
        "description": "Plataforma omnicanal que automatiza la atención al cliente con inteligencia artificial. Conecta WhatsApp Business, Instagram, Facebook, Telegram, TikTok y más en un único CRM con chatbots IA. Meta Business Partner oficial.",
        "foundingDate": "2020",
        "areaServed": "Latinoamérica",
        "email": "hola@runamatic.io",
        "knowsAbout": [
          "Automatización de atención al cliente",
          "Chatbots con inteligencia artificial",
          "WhatsApp Business API",
          "CRM omnicanal",
          "Coexistencia WhatsApp",
          "Automatización de ventas",
          "Meta Business Partner"
        ],
        "sameAs": [
          "https://www.facebook.com/runamatic",
          "https://www.instagram.com/runamatic",
          "https://www.linkedin.com/company/runamatic"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "availableLanguage": "Spanish",
          "email": "hola@runamatic.io"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Runamatic",
        "url": "https://runamatic.io",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "description": "Plataforma de automatización omnicanal para atención al cliente con IA. Automatiza WhatsApp Business sin perder tu número gracias a la coexistencia. Conecta Instagram, Facebook, Telegram, TikTok y más en una sola bandeja de entrada inteligente.",
        "offers": {
          "@type": "AggregateOffer",
          "priceCurrency": "USD",
          "lowPrice": "60",
          "highPrice": "450",
          "offerCount": "2",
          "url": "https://runamatic.io/planes"
        },
        "featureList": [
          "Chatbots con inteligencia artificial (OpenAI GPT-4o)",
          "Bandeja de entrada omnicanal unificada",
          "CRM integrado con seguimiento de contactos",
          "Coexistencia WhatsApp Business — usa la app y el bot al mismo tiempo",
          "Automatización de respuestas 24/7",
          "Mensajería masiva multicanal",
          "Constructor visual de flujos de trabajo",
          "Más de 50 integraciones (Shopify, Stripe, Google Sheets, OpenAI, etc.)",
          "Usuarios ilimitados con roles y permisos"
        ],
        "provider": {
          "@type": "Organization",
          "name": "Runamatic",
          "url": "https://runamatic.io"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "¿Qué es Runamatic?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Runamatic es una plataforma omnicanal que automatiza la atención al cliente con inteligencia artificial. Permite conectar WhatsApp Business, Instagram, Facebook, Telegram, TikTok y más en una única bandeja de entrada con chatbots IA, CRM integrado y automatización de ventas."
            }
          },
          {
            "@type": "Question",
            "name": "¿Cómo automatizo WhatsApp Business sin perder mi número?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Runamatic ofrece tecnología de coexistencia que permite usar tu WhatsApp Business en el teléfono y al mismo tiempo activar los chatbots de automatización. No perdes tu número ni tus conversaciones. Puedes integrar tu cuenta existente de WhatsApp Business o crear una nueva con WhatsApp Cloud API."
            }
          },
          {
            "@type": "Question",
            "name": "¿Cuánto cuesta automatizar WhatsApp con Runamatic?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Los planes de Runamatic comienzan desde $60 USD por mes (Plan Pro), que incluye todas las herramientas de la plataforma con IA integrada (OpenAI GPT-4o) y todos los canales. El Plan Full incluye asesoría completa de estrategia comercial y configuración 100% personalizada por nuestro equipo desde $450 USD por mes."
            }
          },
          {
            "@type": "Question",
            "name": "¿Puedo automatizar Instagram y Facebook con Runamatic?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Sí. Runamatic automatiza mensajes directos, comentarios y respuestas en Instagram y Facebook. Solo se pueden conectar cuentas de tipo empresa o creador. Con un solo sistema gestionas todos los canales desde una bandeja de entrada unificada."
            }
          },
          {
            "@type": "Question",
            "name": "¿Runamatic es Meta Business Partner?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Sí, Runamatic es Meta Business Partner oficial, lo que garantiza integración directa con las APIs oficiales de WhatsApp Business, Instagram y Facebook. Esto asegura estabilidad, cumplimiento de políticas y acceso a las últimas funcionalidades de Meta."
            }
          },
          {
            "@type": "Question",
            "name": "¿Qué canales puedo automatizar con Runamatic?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Runamatic permite automatizar WhatsApp Business, Instagram, Facebook, Telegram, TikTok, SMS, Email, Llamadas telefónicas y Webchat. Todos los canales se gestionan desde una única plataforma con bandeja de entrada omnicanal."
            }
          },
          {
            "@type": "Question",
            "name": "¿Necesito saber programar para usar Runamatic?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "No. Runamatic tiene una interfaz visual sin código. Puedes configurar tu agente de IA escribiendo un prompt en lenguaje natural, crear flujos de trabajo con un constructor drag-and-drop y conectar tus canales en minutos sin necesidad de conocimientos técnicos."
            }
          },
          {
            "@type": "Question",
            "name": "¿Qué integraciones tiene Runamatic?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Runamatic se integra con más de 50 herramientas: OpenAI, Google Sheets, Shopify, WooCommerce, Stripe, PayPal, Mailchimp, HubSpot, Zapier, Google Calendar y muchas más. También cuenta con API y Webhooks para integraciones personalizadas."
            }
          }
        ]
      }
    ]
  };

  return (
    <>
      <SEO {...seoData} />
      <Header />
      <main>
        <HeroSection />
        <VideoSection />
        <ChannelsSection />
        <MetaPartnerSection />
        <DemoSection />
        <PlansSection />
        <ClientsSection />
      </main>
      <Footer />
    </>
  );
};

// Wrapper component that handles hash navigation
const HomePage = () => {
  const location = useLocation();

  useEffect(() => {
    // Handle hash navigation when component mounts or location changes
    if (location.hash) {
      const sectionId = location.hash.substring(1); // Remove the # from hash
      const element = document.getElementById(sectionId);
      if (element) {
        // Add a small delay to ensure page is fully loaded
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  return <HomePageContent />;
};

// Terms Page Component (with Header and Footer)
const TermsPage = () => {
  const seoData = {
    title: 'Términos y Condiciones',
    description: 'Términos y condiciones de uso de la plataforma Runamatic.',
    canonical: '/tyc',
    robots: 'noindex, nofollow' // No indexar en buscadores
  };

  return (
    <>
      <SEO {...seoData} />
      <Header />
      <TermsAndConditions />
      <Footer />
    </>
  );
};

// Privacy Policy Page Component (with Header and Footer)
const PrivacyPolicyPage = () => {
  const seoData = {
    title: 'Política de Privacidad',
    description: 'Política de privacidad de la plataforma Runamatic.',
    canonical: '/privacy-policy',
    robots: 'noindex, nofollow' // No indexar en buscadores
  };

  return (
    <>
      <SEO {...seoData} />
      <Header />
      <PrivacyPolicy />
      <Footer />
    </>
  );
};

// About Us Page Component (with Header and Footer)
const AboutUsPage = () => (
  <>
    <SEO
      title="Sobre Nosotros"
      description="Conoce al equipo detrás de Runamatic. Somos una empresa latinoamericana especializada en automatización de atención al cliente con inteligencia artificial y Meta Business Partner oficial."
      keywords="quienes somos runamatic, equipo runamatic, empresa automatización IA, Meta Business Partner Latinoamérica"
      canonical="/sobre-nosotros"
      schema={{
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": "Sobre Nosotros - Runamatic",
        "url": "https://runamatic.io/sobre-nosotros",
        "description": "Runamatic es una empresa latinoamericana especializada en automatización de atención al cliente con inteligencia artificial.",
        "publisher": { "@type": "Organization", "name": "Runamatic", "url": "https://runamatic.io" }
      }}
    />
    <Header />
    <AboutUs />
    <Footer />
  </>
);

// Careers Page Component (with Header and Footer)
const CareersPage = () => (
  <>
    <SEO
      title="Carreras y Empleos"
      description="Únete al equipo de Runamatic. Trabajamos en el futuro de la automatización con IA para empresas latinoamericanas. Ver posiciones abiertas."
      keywords="trabajo runamatic, empleos automatización IA, carreras tecnología Latinoamérica, trabajo remoto startup IA"
      canonical="/carreras"
      schema={{
        "@context": "https://schema.org",
        "@type": "JobPosting",
        "hiringOrganization": { "@type": "Organization", "name": "Runamatic", "url": "https://runamatic.io" },
        "jobLocation": { "@type": "Place", "address": { "@type": "PostalAddress", "addressCountry": "AR" } },
        "description": "Oportunidades de trabajo en Runamatic, empresa de automatización con IA."
      }}
    />
    <Header />
    <CareersSection />
    <Footer />
  </>
);

// Documentation Page Component (with Header and Footer)
const DocsPage = () => (
  <>
    <SEO
      title="Documentación y Guías"
      description="Documentación completa de Runamatic: guías de configuración, tutoriales de integración con WhatsApp Business, Instagram, Facebook y más. Aprende a configurar tu chatbot IA."
      keywords="documentación runamatic, guías configuración chatbot, tutorial WhatsApp Business API, configurar automatización, integrar WhatsApp empresa"
      canonical="/docs"
      schema={{
        "@context": "https://schema.org",
        "@type": "TechArticle",
        "name": "Documentación Runamatic",
        "url": "https://runamatic.io/docs",
        "description": "Guías y documentación técnica para configurar la plataforma de automatización Runamatic.",
        "publisher": { "@type": "Organization", "name": "Runamatic", "url": "https://runamatic.io" }
      }}
    />
    <Header />
    <DocumentationPage />
    <Footer />
  </>
);

// Document Detail Page Component (with Header and Footer)
const DocDetailPage = () => (
  <>
    <Header />
    <DocumentDetailPage />
    <Footer />
  </>
);

// Changelog Page Component (with Header and Footer)
const ChangelogPageWrapper = () => (
  <>
    <SEO
      title="Novedades y Actualizaciones"
      description="Historial de actualizaciones de Runamatic. Nuevas funcionalidades, mejoras y correcciones en la plataforma de automatización omnicanal."
      keywords="actualizaciones runamatic, novedades plataforma, changelog automatización"
      canonical="/log"
    />
    <Header />
    <ChangelogPage />
    <Footer />
  </>
);

// Thank You Page Component (with Header and Footer)
const ThankYouPageWrapper = () => (
  <>
    <SEO
      title="¡Gracias!"
      description="Gracias por contactar a Runamatic. Te responderemos a la brevedad."
      canonical="/gracias"
      robots="noindex, nofollow"
    />
    <Header />
    <ThankYouPage />
    <Footer />
  </>
);

// Custom Development Page Component (with Header and Footer)
const CustomDevelopmentPageWrapper = () => {
  const seoData = {
    title: 'Desarrollos a Medida - Páginas Web y Aplicaciones',
    description: 'Creamos páginas web modernas y aplicaciones a medida para tu negocio. Desde $200 USD páginas web responsivas, desde $1000 USD aplicaciones personalizadas.',
    keywords: 'desarrollo web, páginas web, aplicaciones a medida, desarrollo software, tiendas online, e-commerce, desarrollo personalizado',
    canonical: '/desarrollos'
  };

  return (
    <>
      <SEO {...seoData} />
      <Header />
      <CustomDevelopmentPage />
      <Footer />
    </>
  );
};

// Campañas Publicitarias Page Component (with Header and Footer)
const CampanasPublicitariasPageWrapper = () => {
  const seoData = {
    title: 'Campañas Publicitarias - Marketing Digital Profesional',
    description: 'Servicios profesionales de campañas publicitarias en Meta y Google Ads. Planes desde USD $180/mes con gestión completa y reportes semanales.',
    keywords: 'campañas publicitarias, marketing digital, meta ads, google ads, facebook ads, instagram ads, publicidad online, gestión de campañas',
    canonical: '/ads'
  };

  return (
    <>
      <SEO {...seoData} />
      <Header />
      <CampanasPublicitariasPage />
      <Footer />
    </>
  );
};

// Blog Page Component (with Header and Footer)
const BlogPageWrapper = () => {
  return (
    <>
      <Header />
      <BlogPage />
      <Footer />
    </>
  );
};

// Blog Post Page Component (with Header and Footer)
const BlogPostPageWrapper = () => {
  return (
    <>
      <Header />
      <BlogPostPage />
      <Footer />
    </>
  );
};

// Not Found Component with Redirect Logic
const NotFoundPage = () => {
  const location = useLocation();

  // Mapeo de URLs obsoletas a nuevas URLs
  const redirectMap = {
    '/docs-category/ia/': '/docs',
    '/docs-category/ia': '/docs',
    '/docs-category/comienzo/': '/docs',
    '/docs-category/comienzo': '/docs',
    '/clientes/': '/#clientes',
    '/clientes': '/#clientes',
    '/contact/': '/#demo',
    '/contact': '/#demo',
    '/about/': '/sobre-nosotros',
    '/about': '/sobre-nosotros'
  };

  // Verificar si la URL actual necesita redirección
  const redirectUrl = redirectMap[location.pathname];
  
  if (redirectUrl) {
    // Si es una redirección a una sección con hash, usar replace
    if (redirectUrl.startsWith('/#')) {
      window.location.replace(redirectUrl);
      return null;
    }
    // Si es una redirección a otra página, usar Navigate
    return <Navigate to={redirectUrl} replace />;
  }

  // Si no es una URL conocida, mostrar página 404
  return (
    <>
      <SEO 
        title="Página no encontrada - Runamatic"
        description="La página que buscas no existe. Encuentra lo que necesitas en nuestras páginas principales."
        canonical={location.pathname}
      />
      <Header />
      <div style={{
        minHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px 20px',
        textAlign: 'center',
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #f9a550 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Shapes de fondo similares al hero */}
        <div style={{
          position: 'absolute',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'rgba(249, 165, 80, 0.1)',
          top: '10%',
          right: '10%',
          animation: 'float 6s ease-in-out infinite'
        }}></div>
        <div style={{
          position: 'absolute',
          width: '150px',
          height: '150px',
          borderRadius: '50%',
          background: 'rgba(249, 165, 80, 0.08)',
          bottom: '20%',
          left: '8%',
          animation: 'float 6s ease-in-out infinite 2s'
        }}></div>
        
        <div style={{
          maxWidth: '600px',
          padding: '50px 40px',
          background: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2)',
          border: '2px solid rgba(249, 165, 80, 0.3)',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{ fontSize: '4em', marginBottom: '20px' }}>
            😢
          </div>
          <h1 style={{ 
            fontSize: '2.5em', 
            marginBottom: '20px',
            fontWeight: '800',
            color: '#ffffff'
          }}>
            404 - Página no encontrada
          </h1>
          <p style={{ 
            fontSize: '1.2em', 
            marginBottom: '20px', 
            opacity: 0.9,
            color: 'rgba(255, 255, 255, 0.9)'
          }}>
            ¡Ups! La página que buscas no existe o ha sido movida.
          </p>
          <p style={{ 
            fontSize: '1em', 
            marginBottom: '40px', 
            opacity: 0.8,
            color: 'rgba(255, 255, 255, 0.8)'
          }}>
            No te preocupes, te ayudamos a encontrar lo que necesitas 🚀
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', justifyContent: 'center' }}>
            <a href="/" style={{
              padding: '15px 30px',
              background: 'linear-gradient(135deg, #f9a550, #ff8c42)',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '50px',
              fontWeight: '700',
              transition: 'all 0.3s ease',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 4px 20px rgba(249, 165, 80, 0.3)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              Ir al inicio
            </a>
            <a href="/sobre-nosotros" style={{
              padding: '15px 30px',
              background: 'transparent',
              color: '#f9a550',
              textDecoration: 'none',
              borderRadius: '50px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              border: '2px solid #f9a550'
            }}>
              Sobre nosotros
            </a>
            <a href="/docs" style={{
              padding: '15px 30px',
              background: 'transparent',
              color: '#f9a550',
              textDecoration: 'none',
              borderRadius: '50px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              border: '2px solid #f9a550'
            }}>
              Documentación
            </a>
          </div>
        </div>
        
        {/* Agregar keyframes para la animación */}
        <style jsx>{`
          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-10px);
            }
          }
        `}</style>
      </div>
      <Footer />
    </>
  );
};

// Wrapper condicional para el ChatWidget (no mostrar en herramientas internas ni landings de ads)
const ConditionalChatWidget = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/presupuestador-runamatic')) return null;
  if (location.pathname.startsWith('/devlanding')) return null;
  return <ChatWidget />;
};

function App() {
  return (
    <Router>
      <ScrollToTop />
      <div className="App">
        <Routes>
          {/* Página principal */}
          <Route path="/" element={<HomePage />} />
          
          {/* Páginas dedicadas para cada sección */}
          <Route path="/planes" element={<PlanesPage />} />
          <Route path="/canales" element={<CanalesPage />} />
          <Route path="/integraciones" element={<IntegracionesPage />} />
          <Route path="/herramientas" element={<HerramientasPage />} />
          <Route path="/preguntas-frecuentes" element={<PreguntasFrecuentesPage />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/desarrollos" element={<CustomDevelopmentPageWrapper />} />
          <Route path="/planFull" element={<PlanRunamaticPage />} />
          <Route path="/planRunamatic" element={<PlanRunamaticPage />} />
          <Route path="/ads" element={<CampanasPublicitariasPageWrapper />} />
          <Route path="/devlanding" element={<DevLandingPage />} />
          
          {/* Páginas existentes */}
          <Route path="/tyc" element={<TermsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/sobre-nosotros" element={<AboutUsPage />} />
          <Route path="/carreras" element={<CareersPage />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/docs/:docId" element={<DocDetailPage />} />
          <Route path="/log" element={<ChangelogPageWrapper />} />
          <Route path="/gracias" element={<ThankYouPageWrapper />} />
          
          {/* Rutas del Blog */}
          <Route path="/blog" element={<BlogPageWrapper />} />
          <Route path="/blog/:slug" element={<BlogPostPageWrapper />} />

          {/* Partners */}
          <Route path="/partners" element={<PartnersPage />} />

          {/* Herramienta interna — no indexada, acceso por URL directa */}
          <Route path="/presupuestador-runamatic" element={<PresupuestadorPage />} />
          
          {/* Catch-all route para URLs no encontradas */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        
        {/* Chat Widget - No aparece en herramientas internas */}
        <ConditionalChatWidget />
      </div>
    </Router>
  );
}

export default App; 